/**
 * VIN validation, on the device.
 *
 * Worth having even though vPIC would tell us the same thing, for three
 * reasons: it works with no signal, it works outside North America where there
 * is no decode service at all, and it catches a typo before a network round
 * trip in a workshop with one bar.
 *
 * The check digit is a real checksum defined by 49 CFR 565. This implementation
 * was verified against NHTSA: a VIN it accepts returns "VIN decoded clean.
 * Check Digit (9th position) is correct."
 */

/** I, O and Q are excluded from VINs precisely because they look like 1 and 0. */
const FORBIDDEN = /[IOQ]/;

const TRANSLIT: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
  J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
  S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};

/** Position 9 is the check digit itself and therefore carries no weight. */
const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

export type VinProblem =
  | 'empty'
  | 'wrong_length'
  | 'forbidden_letter'
  | 'bad_character'
  | 'check_digit';

/**
 * Upper-case and strip the separators people type.
 *
 * Someone reading a VIN off a door jamb copies the spacing they see, and a VIN
 * rejected for a space is a VIN typed again from scratch.
 */
export function normaliseVin(raw: string): string {
  return raw.toUpperCase().replace(/[\s-]/g, '');
}

export function vinCheckDigit(vin: string): string | null {
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const c = vin[i]!;
    const value = c >= '0' && c <= '9' ? Number(c) : TRANSLIT[c];
    if (value === undefined) return null;
    sum += value * WEIGHTS[i]!;
  }
  const remainder = sum % 11;
  return remainder === 10 ? 'X' : String(remainder);
}

/** Null when the VIN is good. Order runs cheapest and most obvious first. */
export function validateVin(raw: string): VinProblem | null {
  const vin = normaliseVin(raw);
  if (vin === '') return 'empty';
  if (vin.length !== 17) return 'wrong_length';
  if (FORBIDDEN.test(vin)) return 'forbidden_letter';
  if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) return 'bad_character';
  if (vinCheckDigit(vin) !== vin[8]) return 'check_digit';
  return null;
}

export function vinProblemMessage(problem: VinProblem, vin: string): string {
  switch (problem) {
    case 'empty': return 'Enter a VIN, or fill the vehicle in by hand.';
    case 'wrong_length':
      return `A VIN is 17 characters — that one is ${normaliseVin(vin).length}.`;
    case 'forbidden_letter':
      return 'VINs never contain I, O or Q. Those are almost always 1 or 0.';
    case 'bad_character': return 'A VIN is letters and digits only.';
    case 'check_digit':
      return 'That VIN fails its own checksum, so a character is wrong somewhere. '
        + 'Check it against the door jamb, or enter the vehicle by hand.';
  }
}
